import isEqual from 'lodash.isequal';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getConvertedDistance, getConvertedElevation } from '@alltrails/data-formatting/distanceConverters';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import CustomRouteActionTaken from '@alltrails/amplitude/enums/CustomRouteActionTaken';
import { CostingsKeys, CustomRouteSegment } from '../../types/CustomRoute';
import { isSegmentRoutable } from '../../utils/customRoutes/validation';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateRouting, setToastMessage, selectCustomRouteActiveSegment } from '../../redux/customRoute';
import useRerouteSegments, { RerouteSegmentsResult } from './useRerouteSegments';
import useMetadata from './useMetadata';

const useAdvancedRouting = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { displayMetric, activity, segment, costingsConfig, costings, isSmartRouting } = useAppSelector(state => ({
    displayMetric: state.context.displayMetric,
    activity: state.customRoute.route.activityType,
    segment: selectCustomRouteActiveSegment(state),
    costingsConfig: state.customRoute.costingsConfig,
    costings: state.customRoute.costings,
    isSmartRouting: state.customRoute.isSmartRouting
  }));

  const rerouteSegments = useRerouteSegments();
  const fetchMetadata = useMetadata();

  const isDisabled = useMemo(() => !segment || !isSegmentRoutable(segment), [segment]);

  const incrementCosting = useCallback(
    async (costing: CostingsKeys, validate?: (currentSegment: CustomRouteSegment, newSegment: CustomRouteSegment) => Promise<boolean>) => {
      if (!segment || !costingsConfig || !costings) {
        return false;
      }

      let currentCosting = costings[costing];
      let valid = false;
      let result: RerouteSegmentsResult | undefined;

      // Automatically increment the costing and reroute until a valid route is found or the maximum costing is reached
      while (!valid) {
        // Note that if we never find a valid route we don't save the updated costing to prevent entries in the undo stack that don't appear to do anything when applied
        // This means future advanced routing actions will start from the last costing value that produced a valid route
        // This is consistent with current mobile behavior
        if (currentCosting >= costingsConfig[costing].maximum) {
          return false;
        }

        currentCosting++;
        result = await rerouteSegments({
          segments: [segment],
          costings: { ...costings, [costing]: currentCosting }
        });

        if (!result.isSuccess || !result.data) {
          return false;
        }

        if (validate) {
          if (result.data.segments && result.data.segments.length > 0) {
            valid = await validate(segment, result.data.segments[0]);
          }
        } else {
          valid = true;
        }
      }

      if (result && result.isSuccess && result.data?.segments && result.data.segments?.length > 0) {
        dispatch(updateRouting({ segment: result.data.segments[0], costings: result.data.costings }));
        return true;
      }

      return false;
    },
    [segment, costingsConfig, costings, rerouteSegments, dispatch]
  );

  const logCustomRouteUpdatedWithMode = useCallback(
    (entryPointLocation: CustomRouteActionTaken) => {
      logCustomRouteUpdated({
        entry_point_location: entryPointLocation,
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: true
      });
    },
    [isSmartRouting]
  );

  const makeShorter = useCallback(async () => {
    let value = 0;
    const result = await incrementCosting('makeShorter', async (currentSegment: CustomRouteSegment, newSegment: CustomRouteSegment) => {
      const { isSuccess, data } = await fetchMetadata(activity, { [newSegment.id]: newSegment.paths.map(path => path.points) });

      if (!isSuccess || !data) {
        return false;
      }
      const currentDistance = currentSegment.distance || 0;
      const newDistance = data.segmentsMetadata[0]?.length || 0;
      value = currentDistance - newDistance;

      return !!currentDistance && !!newDistance && newDistance < currentDistance;
    });

    if (result) {
      logCustomRouteUpdatedWithMode('make shorter');
    }

    dispatch(
      setToastMessage(
        result
          ? {
              iconCode: 'collapse',
              message: intl.formatMessage(
                { defaultMessage: 'Shortened route by {value}' },
                {
                  value: intl.formatNumber(getConvertedDistance(value, displayMetric), {
                    style: 'unit',
                    unit: displayMetric ? 'kilometer' : 'mile',
                    maximumFractionDigits: 1
                  })
                }
              )
            }
          : { message: intl.formatMessage({ defaultMessage: 'No shorter options for current route' }) }
      )
    );
  }, [incrementCosting, dispatch, intl, displayMetric, fetchMetadata, activity, logCustomRouteUpdatedWithMode]);

  const leastGain = useCallback(async () => {
    let value = 0;
    const result = await incrementCosting('leastGain', async (currentSegment: CustomRouteSegment, newSegment: CustomRouteSegment) => {
      const { isSuccess, data } = await fetchMetadata(activity, { [newSegment.id]: newSegment.paths.map(path => path.points) });

      if (!isSuccess || !data) {
        return false;
      }

      const currentElevationGain = currentSegment.elevationGain || 0;
      const newElevationGain = data.segmentsMetadata[0]?.elevationGain || 0;
      value = currentElevationGain - newElevationGain;

      return !!currentElevationGain && !!newElevationGain && newElevationGain < currentElevationGain;
    });

    if (result) {
      logCustomRouteUpdatedWithMode('reduce elevation gain');
    }

    dispatch(
      setToastMessage(
        result
          ? {
              iconCode: 'elevation',
              message: intl.formatMessage(
                { defaultMessage: 'Reduced elevation gain by {value}' },
                {
                  value: intl.formatNumber(getConvertedElevation(value, displayMetric), {
                    style: 'unit',
                    unit: displayMetric ? 'meter' : 'foot',
                    maximumFractionDigits: 0
                  })
                }
              )
            }
          : { message: intl.formatMessage({ defaultMessage: "Can't reduce elevation for current route" }) }
      )
    );
  }, [incrementCosting, dispatch, intl, displayMetric, fetchMetadata, activity, logCustomRouteUpdatedWithMode]);

  const mostScenic = useCallback(async () => {
    const result = await incrementCosting(
      'mostScenic',
      async (currentSegment: CustomRouteSegment, newSegment: CustomRouteSegment) => !isEqual(currentSegment.paths, newSegment.paths)
    );

    if (result) {
      logCustomRouteUpdatedWithMode('take the scenic route');
    }

    dispatch(
      setToastMessage(
        result
          ? { iconCode: 'wildflowers', message: intl.formatMessage({ defaultMessage: 'Added more scenic stops' }) }
          : { message: intl.formatMessage({ defaultMessage: 'No more scenic options for current route' }) }
      )
    );
  }, [incrementCosting, dispatch, intl, logCustomRouteUpdatedWithMode]);

  return {
    isDisabled,
    makeShorter,
    leastGain,
    mostScenic
  };
};

export default useAdvancedRouting;
